import { Row, Col } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
  PercentageOutlined,
  ClockCircleOutlined,
  BarChartOutlined,
  FileTextOutlined,
  WarningOutlined,
  AppstoreOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { MetricCard } from './MetricCard';
import type { MetricsResponse } from '../../api/types';

interface MetricCardGridProps {
  metrics: MetricsResponse | undefined;
  loading: boolean;
}

export function MetricCardGrid({ metrics, loading }: MetricCardGridProps) {
  const formatDuration = (ms: number): string => {
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };

  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} sm={12} lg={6}>
        <MetricCard
          title="Total Jobs"
          value={metrics?.total_jobs ?? 0}
          prefix={<CheckCircleOutlined />}
          trend={metrics?.trends.total_jobs_change}
          loading={loading}
          clickPath="/jobs"
          tooltip="Total unique jobs started in the selected time range"
        />
      </Col>

      <Col xs={24} sm={12} lg={6}>
        <MetricCard
          title="Active Jobs"
          value={metrics?.active_jobs ?? 0}
          prefix={<SyncOutlined spin={!loading && (metrics?.active_jobs ?? 0) > 0} />}
          loading={loading}
          clickPath="/jobs?status=active"
          tooltip="Jobs currently in progress (started but not completed)"
          valueStyle={{ color: '#1890ff' }}
        />
      </Col>

      <Col xs={24} sm={12} lg={6}>
        <MetricCard
          title="Failed Jobs"
          value={metrics?.failed_jobs ?? 0}
          prefix={<CloseCircleOutlined />}
          trend={metrics?.trends.failed_jobs_change}
          loading={loading}
          clickPath="/jobs?status=failed"
          tooltip="Jobs that ended with an exception"
          valueStyle={{ color: metrics?.failed_jobs ? '#ff4d4f' : undefined }}
        />
      </Col>

      <Col xs={24} sm={12} lg={6}>
        <MetricCard
          title="Success Rate"
          value={metrics?.success_rate !== undefined ? metrics.success_rate : '-'}
          suffix={metrics?.success_rate !== undefined ? '%' : undefined}
          prefix={<PercentageOutlined />}
          precision={1}
          trend={metrics?.trends.success_rate_change}
          trendSuffix=" pts"
          loading={loading}
          tooltip="Percentage of jobs that completed successfully"
          valueStyle={{
            color: metrics?.success_rate !== undefined
              ? (metrics.success_rate >= 95 ? '#52c41a' : '#faad14')
              : undefined,
          }}
        />
      </Col>

      <Col xs={24} sm={12} lg={6}>
        <MetricCard
          title="Avg Duration"
          value={metrics ? formatDuration(metrics.avg_duration_ms) : '-'}
          prefix={<ClockCircleOutlined />}
          loading={loading}
          tooltip="Average job execution time"
        />
      </Col>

      <Col xs={24} sm={12} lg={6}>
        <MetricCard
          title="Stat Events"
          value={metrics?.stat_events ?? 0}
          prefix={<BarChartOutlined />}
          loading={loading}
          clickPath="/events?table=stat"
          tooltip="Total stat/metric events recorded"
        />
      </Col>

      <Col xs={24} sm={12} lg={6}>
        <MetricCard
          title="Log Events"
          value={metrics?.log_events ?? 0}
          prefix={<FileTextOutlined />}
          loading={loading}
          clickPath="/events?table=log"
          tooltip="Total log events recorded"
        />
      </Col>

      <Col xs={24} sm={12} lg={6}>
        <MetricCard
          title="Error Count"
          value={metrics?.error_count ?? 0}
          prefix={<WarningOutlined />}
          loading={loading}
          clickPath="/events?table=log&level=ERROR"
          tooltip="Total ERROR and FATAL log events"
          valueStyle={{ color: metrics?.error_count ? '#ff4d4f' : undefined }}
        />
      </Col>

      <Col xs={24} sm={12} lg={6}>
        <MetricCard
          title="Total Events"
          value={metrics?.total_events ?? 0}
          prefix={<UnorderedListOutlined />}
          loading={loading}
          clickPath="/events?table=job_status"
          tooltip="Total job status events (all event types)"
        />
      </Col>

      <Col xs={24} sm={12} lg={6}>
        <MetricCard
          title="Top Namespace"
          value={metrics?.top_namespaces[0]?.namespace ?? '-'}
          prefix={<AppstoreOutlined />}
          loading={loading}
          tooltip="Most active namespace by job count"
          clickPath={
            metrics?.top_namespaces[0]
              ? `/jobs?namespace=${metrics.top_namespaces[0].namespace}`
              : undefined
          }
        />
      </Col>
    </Row>
  );
}
